<template>
  <Teleport to="body">
    <div class="login-mask" @click.self="handleClose">
      <div class="login-panel-border-bg">
        <div class="login-panel">
          <header class="panel-header">
            <div class="title-block">
              <h3 class="title">登录您的账号</h3>
              <p class="subtitle">输入您的手机号进行登录，新用户会自行给你注册</p>
            </div>
          </header>
          <div class="panel-body">
            <label class="field">
              <span class="field-label">手机号</span>
              <div class="input-wrapper">
                <input
                    v-model.trim="phone"
                    type="tel"
                    maxlength="11"
                    placeholder="请输入手机号"
                    class="field-input"
                    :class="{ error: phoneError }"
                    @input="phoneError = ''"
                />
                <img
                    v-if="phone"
                    :src="cleanIcon"
                    alt="清除"
                    class="clean-icon"
                    @click="handleClearPhone"
                />
              </div>
              <span v-if="phoneError" class="error-message">{{ phoneError }}</span>
            </label>

            <label class="field code-field">
              <span class="field-label">验证码</span>
              <div class="code-input-row">
                <div class="input-wrapper">
                  <input
                      v-model.trim="code"
                      type="text"
                      maxlength="6"
                      placeholder="请输入验证码"
                      class="field-input"
                      :class="{ error: codeError }"
                      @input="codeError = ''"
                  />
                  <img
                      v-if="code"
                      :src="cleanIcon"
                      alt="清除"
                      class="clean-icon"
                      @click="handleClearCode"
                  />
                </div>
                <button
                    class="send-button"
                    :class="{ enabled: canSendCode && !sendingCode }"
                    :disabled="!canSendCode || sendingCode"
                    @click="handleSendCode"
                >
                  {{ sendingCode ? '发送中...' : sendButtonText }}
                </button>
              </div>
              <span v-if="codeError" class="error-message">{{ codeError }}</span>
            </label>

            <button
                class="primary-button"
                :class="{ enabled: canSubmit && !submitting }"
                :disabled="!canSubmit || submitting"
                @click="handleSubmit"
            >
              {{ submitting ? '登录中...' : '登录注册' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineEmits */
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {sendSmsCode, smsLogin, SUCCESS_CODE, LOGIN_SUCCESS} from '@/api/api';
import {Message} from '@/utils/message';
import cleanIcon from '@/assets/clean_icon.png';

const emit = defineEmits(['close', 'success']);

const COUNTDOWN_MS = 60 * 1000;
const STORAGE_KEY = 'lk_login_sms_expire_at';

const phone = ref('');
const code = ref('');
const remainingMs = ref(0);
const sendingCode = ref(false);
const submitting = ref(false);
const phoneError = ref('');
const codeError = ref('');
let timer = null;

const phoneValid = computed(() => /^1[3-9]\d{9}$/.test(phone.value));
const canSubmit = computed(() => phoneValid.value && code.value.trim().length >= 6);
const canSendCode = computed(() => phoneValid.value && remainingMs.value <= 0);
const sendButtonText = computed(() => {
  if (remainingMs.value > 0) {
    return `${Math.ceil(remainingMs.value / 1000)}s`;
  }
  return '获取验证码';
});

const updateRemaining = () => {
  const expire = Number(localStorage.getItem(STORAGE_KEY)) || 0;
  const now = Date.now();
  const diff = expire - now;
  remainingMs.value = diff > 0 ? diff : 0;
  if (remainingMs.value <= 0 && expire) {
    localStorage.removeItem(STORAGE_KEY);
  }
};

const startTick = () => {
  updateRemaining();
  if (timer) clearInterval(timer);
  timer = setInterval(updateRemaining, 1000);
};

const handleSendCode = async () => {
  if (!canSendCode.value || sendingCode.value) return;
  sendingCode.value = true;
  try {
    const res = await sendSmsCode(phone.value);
    if (res.code === SUCCESS_CODE) {
      Message({
        message: res.msg || '验证码发送成功',
        type: 'success',
        duration: 3000
      });
      const expireAt = Date.now() + COUNTDOWN_MS;
      localStorage.setItem(STORAGE_KEY, `${expireAt}`);
      remainingMs.value = COUNTDOWN_MS;
      startTick();
    } else {
      Message({
        message: res.msg || '验证码发送失败',
        type: 'error',
        duration: 3000
      });
    }
  } catch (error) {
    Message({
      message: '验证码发送失败，请稍后重试',
      type: 'error',
      duration: 3000
    });
  } finally {
    sendingCode.value = false;
  }
};

const handleSubmit = async () => {
  if (!canSubmit.value || submitting.value) return;

  submitting.value = true;
  phoneError.value = '';
  codeError.value = '';
  try {
    const res = await smsLogin(phone.value, code.value);
    if (res.code === SUCCESS_CODE || res.code === LOGIN_SUCCESS) {
      Message({
        message: res.msg || '登录成功',
        type: 'success',
        duration: 3000
      });
      emit('success', res);
      handleClose();
    } else {
      // 显示错误信息在输入框底部
      const errorMsg = res.msg || '登录失败';
      codeError.value = errorMsg;
      Message({
        message: errorMsg,
        type: 'error',
        duration: 3000
      });
    }
  } catch (error) {
    const errorMsg = '登录失败，请稍后重试';
    codeError.value = errorMsg;
    Message({
      message: errorMsg,
      type: 'error',
      duration: 3000
    });
  } finally {
    submitting.value = false;
  }
};

const handleClearPhone = () => {
  phone.value = '';
  phoneError.value = '';
};

const handleClearCode = () => {
  code.value = '';
  codeError.value = '';
};

const handleClose = () => {
  emit('close');
};

onMounted(() => {
  startTick();
});

onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
});
</script>

<style scoped>
.login-mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.login-panel-border-bg {
  background: red;
  padding: 1px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
}

.login-panel {
  width: 460px;
  padding: 28px 28px 26px;
  border-radius: 18px;
  background: rgba(27, 24, 19, 0.70);
  backdrop-filter: blur(4px);
  color: #faf9f6;
  box-sizing: border-box;
}

.panel-header {
  margin-bottom: 20px;
  width: 100%;
  text-align: center;
}

.title-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.eyebrow {
  margin: 0;
  font-size: 12px;
  letter-spacing: 1px;
  color: #f0c376;
}

.title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #faf9f6;
}

.subtitle {
  margin: 0;
  color: #c3b8a8;
  font-size: 14px;
}

.icon-button {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #faf9f6;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  display: grid;
  place-items: center;
  transition: background 0.2s ease, border-color 0.2s ease;
}


.panel-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  color: #d0ccc1;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.field-input {
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #faf9f6;
  padding: 0 14px;
  padding-right: 40px;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: rgba(240, 195, 118, 0.8);
  box-shadow: 0 0 0 3px rgba(240, 195, 118, 0.16);
}

.field-input.error {
  border-color: #ff4444;
}

.field-input.error:focus {
  border-color: #ff4444;
  box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.16);
}

.field-input::placeholder {
  color: #8f857a;
}

.clean-icon {
  position: absolute;
  right: 14px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.clean-icon:hover {
  opacity: 1;
}

.error-message {
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
}

.code-field .code-input-row {
  display: grid;
  grid-template-columns: 1fr 90px;
  gap: 12px;
  width: 100%;
}

.code-field .input-wrapper {
  width: 100%;
}

.send-button {
  display: flex;
  width: 90px;
  height: 48px;
  padding: 12px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  border: 1px solid #dfe1e7;
  background: #fff;
  color: #7b7f88;
  cursor: not-allowed;
  font-weight: 400;
  font-size: 14px;
  transition: transform 0.1s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.send-button.enabled {
  cursor: pointer;
  color: #060606;
  border: 1px solid #dfe1e7;
  background: #fff;
  box-shadow: none;
}

.send-button:disabled {
  opacity: 1;
}

.primary-button {
  height: 52px;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #2a231c;
  color: #7d7369;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
  cursor: not-allowed;
  box-shadow: none;
  transition: transform 0.1s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.primary-button.enabled {
  cursor: pointer;
  color: #120f0d;
  border: 1px solid #ddb463;
  background: linear-gradient(67deg, #bc823a 0%, #f7cf73 52%, #e39c18 100%);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.32);
}

.primary-button:active.enabled {
  transform: translateY(1px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.26);
}

.tips {
  margin: 0;
  text-align: center;
  color: #9d9388;
  font-size: 13px;
}
</style>
